import { createApi } from '@reduxjs/toolkit/query/react';
import {
  DefaultResponse,
  DefaultResposeWithPagination,
  RequestWithPagination,
} from '../helpers/types';
import {
  Buyer,
  BuyersDealsCount,
  CreateDealPayload,
  CreateDocumentOfDealpayload,
  CreateAndUpdateFactorsBranchPayload,
  CreateTransaction,
  CreateTransactionPayload,
  DealCountsStatus,
  DealRequest,
  DealsCount,
  DealsSum,
  EditDealDetailsPayload,
  Factor,
  FactorDealStatus,
  FactorLimit,
  FactorLimitActivity,
  FactorLimitStatus,
  FactorReport,
  GetClientsList,
  GetFactorDealsParams,
  GetFactorReportPayload,
  GetOKEDList,
  GetTransactions,
  IFactorsBranch,
  ISettings,
  UpdateFactorsSettingsPayload,
  CreateFactorsConditionPayload,
  GetFactorsConditionsResponse,
  FactorBranch,
  DefaultDictionary,
  GetNotificationsResponse,
  Currency,
  ForeignContractor,
  CreateForeignContractorPayload,
} from './types';
import {
  AdditionalDataResponse,
  BriefDataResponse,
} from '@/entities/ihamkor/types';
import { baseQueryWithReauth } from './baseQuery';

export const api = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'FactorLimit',
    'FactorDeal',
    'FactorDeals',
    'DealAction',
    'FactorLimits',
    'FactorsBranches',
    'Notifications',
    'ForeignContractor',
  ],
  endpoints: (builder) => ({
    getSellerRequests: builder.query<
      DefaultResposeWithPagination<DealRequest> & { dealsCount: DealsCount } & {
        dealsSum: DealsSum;
      },
      RequestWithPagination
    >({
      query: (params) => ({
        url: '/sellers/deals',
        method: 'GET',
        params,
      }),
      providesTags: ['DealAction'],
    }),
    getFactors: builder.query<
      DefaultResponse<Factor[]>,
      { companyTin?: string; international?: boolean; currency?: Currency }
    >({
      query: ({ companyTin, international, currency }) => ({
        url: '/factors',
        method: 'GET',
        params: international
          ? { international: true, currency }
          : { companyTin },
      }),
    }),
    getFactorBuyers: builder.query<DefaultResponse<Buyer[]>, unknown>({
      query: () => ({
        url: `/factors/buyers`,
        method: 'GET',
      }),
    }),
    getDealNumber: builder.query<DefaultResponse<{ number: number }>, unknown>({
      query: () => ({
        url: '/sellers/deals/number/next',
        method: 'GET',
      }),
    }),
    createSellerDeal: builder.mutation<
      DefaultResponse<DealRequest>,
      CreateDealPayload
    >({
      query: (body) => ({
        method: 'POST',
        url: '/sellers/deals',
        body,
      }),
      invalidatesTags: ['DealAction'],
    }),
    getSellerRequestById: builder.query<
      DefaultResponse<DealRequest> & { factor: { branch: FactorBranch } },
      string
    >({
      query: (id) => ({
        url: `/sellers/deals/${id}`,
        method: 'GET',
      }),
      providesTags: ['DealAction'],
    }),
    sendSellerRequest: builder.mutation<
      DefaultResponse,
      { signature: string; unique: number }
    >({
      query: ({ signature, unique }) => ({
        url: `/sellers/deals/${unique}/send`,
        method: 'POST',
        body: { signature },
      }),
      invalidatesTags: ['DealAction'],
    }),
    // editSellerRequest: builder.mutation<
    //   DefaultResponse<DealRequest>,
    //   { unique: number; body: CreateDealPayload }
    // >({
    //   query: ({ unique, body }) => ({
    //     url: `/sellers/deals/${unique}`,
    //     method: "PUT",
    //     body,
    //   }),
    // }),
    // deleteSellerRequest: builder.mutation<DefaultResponse, { unique: number }>({
    //   query: ({ unique }) => ({
    //     url: `/sellers/deals/${unique}`,
    //     method: "DELETE",
    //   }),
    // }),
    editSellerRequest: builder.mutation<
      DefaultResponse<DealRequest>,
      { unique: number; body: CreateDealPayload }
    >({
      query: ({ unique, body }) => ({
        url: `/sellers/deals/${unique}/edit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DealAction'],
    }),
    deleteSellerRequest: builder.mutation<DefaultResponse, { unique: number }>({
      query: ({ unique }) => ({
        url: `/sellers/deals/${unique}/delete`,
        method: 'POST',
      }),
      invalidatesTags: ['DealAction'],
    }),
    getBuyersIncomingRequests: builder.query<
      DefaultResposeWithPagination<DealRequest> & {
        dealsCount: BuyersDealsCount;
      },
      RequestWithPagination
    >({
      query: (params) => ({
        url: '/buyers/deals',
        method: 'GET',
        params,
      }),
      providesTags: ['DealAction'],
    }),
    getBuyersIncomingRequest: builder.query<
      DefaultResponse<DealRequest> & { factor: { branch: FactorBranch } },
      string
    >({
      query: (id) => ({
        url: `/buyers/deals/${id}`,
        method: 'GET',
      }),
      providesTags: ['DealAction'],
    }),
    getFactorDeals: builder.query<
      DefaultResposeWithPagination<DealRequest> & {
        dealsCount: Record<DealCountsStatus & string, number>;
        dealSum: {
          sumContractAmount: number;
          sumSellerReceivedAmount: number;
          sumBuyerPaidAmount: number;
          sumFactorCommission: number;
        };
        contractAmount: number;
        sellerReceivedAmount: number;
        factorCommission: number;
        totalPayoutAmount: number;
      },
      Partial<RequestWithPagination<GetFactorDealsParams>>
    >({
      query: (params) => ({
        url: '/factors/deals',
        method: 'GET',
        params,
      }),
      providesTags: ['FactorDeals'],
    }),
    getFactorDealRequest: builder.query<
      DefaultResponse<DealRequest> & { factor: { branch: FactorBranch } },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `/factors/deals/${id}`,
        method: 'GET',
      }),
      providesTags: ['FactorDeal'],
    }),
    getFactorLimits: builder.query<
      DefaultResposeWithPagination<FactorLimit> & {
        limitsCount: {
          active: number;
          overdue: number;
          suspended: number;
        };
        redeemedDealsAmount: number;
      },
      Partial<
        RequestWithPagination<{
          status?: FactorLimitStatus;
          search?: string;
        }>
      >
    >({
      query: (params) => ({
        url: '/factors/limits',
        method: 'GET',
        params,
      }),
      providesTags: ['FactorLimits'],
    }),
    getFactorReport: builder.query<
      DefaultResponse<FactorReport>,
      Partial<GetFactorReportPayload>
    >({
      query: (body) => ({
        url: '/factors/reports/factorReport',
        method: 'POST',
        body,
      }),
    }),
    getClientsList: builder.query<DefaultResponse<GetClientsList[]>, unknown>({
      query: () => ({
        url: '/factors/clientsList',
        method: 'GET',
      }),
    }),
    getOKEDList: builder.query<DefaultResponse<GetOKEDList[]>, unknown>({
      query: () => ({
        url: '/lists/OKED',
        method: 'GET',
      }),
    }),
    getTransactions: builder.query<
      DefaultResposeWithPagination<GetTransactions> & {
        transactionsSum: {
          payment: number;
          repayment: number;
          sumFactorCommission: number;
          avgFactorDiscount: number;
        };
      },
      RequestWithPagination<{
        dateFrom: string;
        dateTo: string;
        clientTin: string;
      }>
    >({
      query: () => ({
        url: '/transactions',
        method: 'GET',
      }),
    }),
    createTransaction: builder.query<
      CreateTransaction,
      CreateTransactionPayload
    >({
      query: (body) => ({
        url: '/transactions',
        method: 'POST',
        body,
      }),
    }),
    getTransaction: builder.query<CreateTransaction, { id: string }>({
      query: ({ id }) => ({
        url: `/transactions/${id}`,
        method: 'GET',
      }),
    }),
    getFactorSingleLimit: builder.query<
      DefaultResponse<
        FactorLimit & {
          stats: {
            totalDeals: 0;
            totalTransactions: 0;
          };
          generalAgreement: {
            number: string | null;
            date: string | null;
            didoxId: string | null;
          };
        }
      >,
      string
    >({
      query: (id) => ({
        url: `/factors/limits/${id}`,
        method: 'GET',
      }),
      providesTags: ['FactorLimit'],
    }),
    getFactorLimitsActivities: builder.query<
      DefaultResponse<FactorLimitActivity[]>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/factors/limits/${id}/activities`,
        method: 'GET',
      }),
      providesTags: ['FactorLimit'],
    }),
    factorLimitChange: builder.mutation<
      DefaultResponse<FactorLimit>,
      {
        amount: number;
        detail: string;
        id: number;
        branchId: number | null;
        generalAgreementDocumentNumber?: string;
        generalAgreementDocumentDate?: string | null;
        generalAgreementState?: 'notRequired' | 'exists' | 'needToCreate';
        currency: Currency;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/factors/limits/${id}/change`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FactorLimit'],
    }),
    suspendFactorLimit: builder.mutation<
      DefaultResponse<FactorLimit>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/factors/limits/${id}/suspend`,
        method: 'POST',
      }),
      invalidatesTags: ['FactorLimit'],
    }),
    resumeFactorLimit: builder.mutation<
      DefaultResponse<FactorLimit>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/factors/limits/${id}/resume`,
        method: 'POST',
      }),
      invalidatesTags: ['FactorLimit'],
    }),
    deleteFactorLimit: builder.mutation<
      DefaultResponse,
      { id: number; signature: string }
    >({
      query: ({ id, signature }) => ({
        url: `/factors/limits/${id}/delete`,
        method: 'POST',
        body: { signature },
      }),
      invalidatesTags: ['FactorLimits'],
    }),
    getLimitsStatuses: builder.query<
      DefaultResponse<
        {
          unique: string;
          name: string;
        }[]
      >,
      unknown
    >({
      query: () => ({
        url: `/factors/limits/statuses`,
        method: 'GET',
      }),
    }),
    getFactorsDealsStatuses: builder.query<
      DefaultResponse<
        {
          unique: string;
          name: FactorDealStatus;
        }[]
      >,
      unknown
    >({
      query: () => ({
        url: `/factors/deals/statuses`,
        method: 'GET',
      }),
    }),
    createFactorLimit: builder.mutation<
      DefaultResponse<FactorLimit>,
      {
        buyer: string;
        amount: number;
        branchId: number | null;
        generalAgreementDocumentNumber?: string;
        generalAgreementDocumentDate?: string | null;
        generalAgreementState: 'notRequired' | 'exists' | 'needToCreate';
        currency: Currency;
      }
    >({
      query: (body) => ({
        url: '/factors/limits',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FactorLimits'],
    }),
    requestClarification: builder.mutation<
      DefaultResponse,
      { comment: string; id: number }
    >({
      query: ({ id, comment }) => ({
        url: `/factors/deals/${id}/clarifications`,
        method: 'POST',
        body: { comment },
      }),
      invalidatesTags: ['FactorDeal'],
    }),
    rejectDealRequest: builder.mutation<
      DefaultResponse,
      { id: number; signature: string; reason: string }
    >({
      query: ({ id, signature, reason }) => ({
        url: `/factors/deals/${id}/decline`,
        method: 'POST',
        body: { signature, reason },
      }),
      invalidatesTags: ['FactorDeal', 'FactorDeals'],
    }),
    answerOnClarification: builder.mutation<
      DefaultResponse,
      { id: number; answer: string }
    >({
      query: ({ id, answer }) => ({
        url: `/clarifications/${id}/answer`,
        method: 'POST',
        body: { answer },
      }),
      invalidatesTags: ['DealAction'],
    }),
    createDealDocument: builder.mutation<
      DefaultResponse<{ didoxId: string }>,
      CreateDocumentOfDealpayload
    >({
      query: (body) => ({
        url: '/documents',
        method: 'POST',
        body,
        headers: {
          ['user-key']: localStorage.getItem('userKey') || '',
        },
      }),
    }),
    signDealDocument: builder.mutation<
      DefaultResponse,
      { id: number | string; signature: string }
    >({
      query: ({ id, signature }) => ({
        url: `/documents/sign/${id}`,
        method: 'POST',
        body: { signature },
        headers: {
          ['user-key']: localStorage.getItem('userKey') || '',
        },
      }),
      invalidatesTags: ['DealAction'],
    }),
    rejectDealDocument: builder.mutation<
      DefaultResponse,
      { id: number | string; signature: string; comment: string }
    >({
      query: ({ id, signature, comment }) => ({
        url: `/documents/reject/${id}`,
        method: 'POST',
        body: { signature, comment },
        headers: {
          ['user-key']: localStorage.getItem('userKey') || '',
        },
      }),
      invalidatesTags: ['FactorDeal', 'DealAction'],
    }),

    getBriefData: builder.query<DefaultResponse<BriefDataResponse>, string>({
      query: (tin) => ({
        url: '/companies/briefdata',
        method: 'POST',
        body: { tin },
      }),
    }),
    getAdditionalData: builder.query<
      DefaultResponse<AdditionalDataResponse>,
      { unique: number; tin: string }
    >({
      query: (body) => ({
        url: '/companies/additionaldata',
        method: 'POST',
        body,
      }),
    }),
    getRecourseTypes: builder.query<
      DefaultResponse<{ id: string; name: string }[]>,
      void
    >({
      query: () => ({
        url: '/factors/deals/recourseTypes',
        method: 'GET',
      }),
    }),
    editFactorsDeal: builder.mutation<
      DefaultResponse,
      EditDealDetailsPayload & { id: number }
    >({
      query: ({ id, ...body }) => ({
        url: `/factors/deals/${id}/edit`,
        method: 'POST',
        body,
      }),
    }),
    deleteDealDocument: builder.mutation<void, { dealId: number; id: string }>({
      query: ({ dealId, id }) => ({
        url: `/documents/delete/${id}`,
        method: 'POST',
        body: { dealId },
        headers: {
          ['user-key']: localStorage.getItem('userKey') || '',
        },
      }),
      invalidatesTags: ['DealAction'],
    }),
    getFactorRequestsStatuses: builder.query<
      DefaultResponse<
        {
          unique: string;
          name: string;
        }[]
      >,
      void
    >({
      query: () => ({
        url: '/factors/requests/statuses',
        method: 'GET',
      }),
    }),
    getFactorRequests: builder.query<
      DefaultResposeWithPagination<DealRequest> & {
        dealsCount: Record<DealCountsStatus & string, number>;
        dealSum: {
          sumContractAmount: number;
          sumSellerReceivedAmount: number;
          sumBuyerPaidAmount: number;
          sumFactorCommission: number;
        };
        contractAmount: number;
        sellerReceivedAmount: number;
        factorCommission: number;
      },
      Partial<RequestWithPagination<GetFactorDealsParams>>
    >({
      query: (params) => ({
        url: '/factors/requests',
        method: 'GET',
        params,
      }),
    }),
    getCommissionCalculationTypes: builder.query<
      DefaultResponse<DefaultDictionary<'id'>[]>,
      void
    >({
      query: () => ({
        url: '/factors/deals/comissionCalculationTypes',
        method: 'GET',
      }),
    }),
    getBuyerDealNumber: builder.query<DefaultResponse<{ number: number }>, {}>({
      query: () => ({
        url: '/buyers/deals/number/next',
        method: 'GET',
      }),
    }),
    createBuyerDeal: builder.mutation<
      DefaultResponse<DealRequest>,
      CreateDealPayload
    >({
      query: (body) => ({
        method: 'POST',
        url: '/buyers/deals',
        body,
      }),
      invalidatesTags: ['DealAction'],
    }),
    sendBuyerRequest: builder.mutation<
      DefaultResponse,
      { signature: string; unique: number }
    >({
      query: ({ signature, unique }) => ({
        url: `/buyers/deals/${unique}/send`,
        method: 'POST',
        body: { signature },
      }),
      invalidatesTags: ['DealAction'],
    }),
    editBuyerRequest: builder.mutation<
      DefaultResponse<DealRequest>,
      { unique: number; body: CreateDealPayload }
    >({
      query: ({ unique, body }) => ({
        url: `/buyers/deals/${unique}/edit`,
        method: 'POST',
        body,
      }),
    }),
    getIncomingDeals: builder.query<
      DefaultResposeWithPagination<DealRequest> & { dealsCount: DealsCount } & {
        dealsSum: DealsSum;
      },
      RequestWithPagination<{
        status: string;
        factoringType?: string;
        currency?: Currency;
      }>
    >({
      query: (params) => ({
        url: '/deals/incoming',
        method: 'GET',
        params,
      }),
      providesTags: ['DealAction'],
    }),
    getOutgoingDeals: builder.query<
      DefaultResposeWithPagination<DealRequest> & { dealsCount: DealsCount } & {
        dealsSum: DealsSum;
      },
      RequestWithPagination<{
        status: string;
        factoringType?: string;
        currency?: Currency;
      }>
    >({
      query: (params) => ({
        url: '/deals/outgoing',
        method: 'GET',
        params,
      }),
      providesTags: ['DealAction'],
    }),
    getFactorsBranches: builder.query<DefaultResponse<IFactorsBranch[]>, void>({
      query: () => ({
        url: '/factors/branches',
        method: 'GET',
      }),
      providesTags: ['FactorsBranches'],
      keepUnusedDataFor: 3600,
    }),
    deleteFactorsBranch: builder.mutation<DefaultResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/factors/branches/${id}/delete`,
        method: 'POST',
      }),
      invalidatesTags: ['FactorsBranches'],
    }),
    createFactorsBranch: builder.mutation<
      DefaultResponse,
      CreateAndUpdateFactorsBranchPayload
    >({
      query: (body) => ({
        url: '/factors/branches',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FactorsBranches'],
    }),
    getFactorsBranchInfo: builder.query<
      DefaultResponse<IFactorsBranch>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/factors/branches/${id}`,
        method: 'GET',
      }),
    }),
    updateFactorsBranch: builder.mutation<
      DefaultResponse,
      { id: number; body: CreateAndUpdateFactorsBranchPayload }
    >({
      query: ({ id, body }) => ({
        url: `factors/branches/${id}/edit`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['FactorsBranches'],
    }),
    getAttachedDealDocuments: builder.query<
      DefaultResponse<{ id: string; name: string }[]>,
      void
    >({
      query: () => ({
        url: '/factors/settings/attchedDealDocuments',
        method: 'GET',
      }),
    }),
    getContractPartiesCompositions: builder.query<
      DefaultResponse<{ id: string; name: string }[]>,
      void
    >({
      query: () => ({
        url: '/factors/settings/contractPartiesCompositions',
        method: 'GET',
      }),
    }),
    getFactorsSettings: builder.query<DefaultResponse<ISettings>, void>({
      query: () => ({
        url: '/factors/settings',
        method: 'GET',
      }),
    }),
    updateFactorsSettings: builder.mutation<
      DefaultResponse<ISettings>,
      UpdateFactorsSettingsPayload
    >({
      query: (body) => ({
        url: '/factors/settings',
        method: 'POST',
        body,
      }),
    }),
    createFactorsCondition: builder.mutation<
      DefaultResponse,
      CreateFactorsConditionPayload
    >({
      query: (body) => ({
        url: '/factors/settings/conditions/create',
        method: 'POST',
        body,
      }),
    }),
    getFactorsConditions: builder.query<
      DefaultResponse<GetFactorsConditionsResponse[]>,
      void
    >({
      query: (body) => ({
        url: 'factors/settings/conditions',
        method: 'POST',
        body,
      }),
    }),
    deleteFactorsCondition: builder.mutation<DefaultResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/factors/settings/conditions/${id}/delete`,
        method: 'POST',
      }),
    }),
    getFactorsConditionInfo: builder.query<
      DefaultResponse<GetFactorsConditionsResponse>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/factors/settings/conditions/${id}`,
        method: 'GET',
      }),
    }),
    updateFactorsCondition: builder.mutation<
      DefaultResponse<GetFactorsConditionsResponse>,
      { id: number; body: CreateFactorsConditionPayload }
    >({
      query: ({ id, body }) => ({
        url: `factors/settings/conditions/${id}/edit`,
        method: 'POST',
        body,
      }),
    }),
    getFactoringContractTemplate: builder.query<
      { base64: string },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `factors/deals/${id}/factoringContract`,
        method: 'GET',
      }),
    }),
    getRegisterOfDebtsTemplate: builder.query<
      { base64: string },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `factors/deals/${id}/registerOfDebts`,
        method: 'GET',
      }),
    }),
    getGeneralAgreement: builder.query<{ base64: string }, { id: number }>({
      query: ({ id }) => ({
        url: `factors/limits/${id}/generalAgreement`,
        method: 'GET',
      }),
    }),
    getGeneralAgreementV2: builder.query<
      { base64: string },
      {
        buyer: string;
        amount: number;
        branchId: number | null;
        generalAgreementDocumentNumber: string;
        generalAgreementDocumentDate: string | null;
      }
    >({
      query: (body) => ({
        url: `factors/limits/generalAgreement/v2`,
        method: 'POST',
        body,
      }),
    }),
    deleteGeneralAgreement: builder.mutation<
      DefaultResponse<{ message: string }>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/factors/limits/${id}/generalAgreement/delete`,
        method: 'GET',
      }),
    }),
    assignFactorDealBranch: builder.mutation<
      DefaultResponse<DealRequest>,
      { id: number; branchId: number }
    >({
      query: ({ id, branchId }) => ({
        url: `/factors/deals/${id}/assignBranch`,
        method: 'POST',
        body: { branchId },
      }),
      invalidatesTags: ['FactorDeal', 'FactorDeals'],
    }),
    getNotifications: builder.query<GetNotificationsResponse, unknown>({
      query: () => ({
        url: 'exec/getNotifications',
        method: 'GET',
      }),
      providesTags: ['Notifications'],
    }),
    viewNotification: builder.mutation<{ success: boolean }, number>({
      query: (unique) => ({
        url: 'exec/viewNotification',
        method: 'POST',
        params: { unique },
      }),
      invalidatesTags: ['Notifications'],
    }),
    viewAllNotifications: builder.mutation<{ success: boolean }, void>({
      query: () => ({
        url: 'exec/viewAllNotifications',
        method: 'POST',
      }),
      invalidatesTags: ['Notifications'],
    }),
    getForeignContractors: builder.query<
      DefaultResponse<ForeignContractor[]>,
      void
    >({
      query: () => ({
        url: '/foreign-contractors',
        method: 'GET',
      }),
      providesTags: ['ForeignContractor'],
    }),
    createForeignContractor: builder.mutation<
      DefaultResponse<ForeignContractor>,
      CreateForeignContractorPayload
    >({
      query: (body) => ({
        url: '/foreign-contractors',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ForeignContractor'],
    }),
  }),
});

export const {
  useGetSellerRequestsQuery,
  useGetFactorsQuery,
  useLazyGetFactorsQuery,
  useGetFactorBuyersQuery,
  useLazyGetFactorBuyersQuery,
  useGetDealNumberQuery,
  useCreateSellerDealMutation,
  useGetBuyersIncomingRequestsQuery,
  useLazyGetBuyersIncomingRequestsQuery,
  useGetBuyersIncomingRequestQuery,
  useLazyGetBuyersIncomingRequestQuery,
  useGetSellerRequestByIdQuery,
  useSendSellerRequestMutation,
  useEditSellerRequestMutation,
  useDeleteSellerRequestMutation,
  useGetFactorDealsQuery,
  useGetFactorLimitsQuery,
  useGetFactorReportQuery,
  useGetClientsListQuery,
  useLazyGetClientsListQuery,
  useGetOKEDListQuery,
  useLazyGetOKEDListQuery,
  useGetFactorSingleLimitQuery,
  useGetFactorLimitsActivitiesQuery,
  useFactorLimitChangeMutation,
  useSuspendFactorLimitMutation,
  useGetTransactionsQuery,
  useCreateTransactionQuery,
  useGetTransactionQuery,
  useLazyCreateTransactionQuery,
  useLazyGetTransactionQuery,
  useLazyGetDealNumberQuery,
  useLazyGetFactorLimitsQuery,
  useLazyGetFactorDealsQuery,
  useLazyGetFactorLimitsActivitiesQuery,
  useLazyGetSellerRequestsQuery,
  useLazyGetFactorSingleLimitQuery,
  useLazyGetSellerRequestByIdQuery,
  useLazyGetTransactionsQuery,
  useGetLimitsStatusesQuery,
  useLazyGetLimitsStatusesQuery,
  useGetFactorsDealsStatusesQuery,
  useLazyGetFactorsDealsStatusesQuery,
  useCreateFactorLimitMutation,
  useGetFactorDealRequestQuery,
  useLazyGetFactorDealRequestQuery,
  useRequestClarificationMutation,
  useRejectDealRequestMutation,
  useAnswerOnClarificationMutation,
  useCreateDealDocumentMutation,
  useSignDealDocumentMutation,
  useRejectDealDocumentMutation,
  useResumeFactorLimitMutation,
  useDeleteFactorLimitMutation,
  useGetBriefDataQuery,
  useGetAdditionalDataQuery,
  useGetRecourseTypesQuery,
  useEditFactorsDealMutation,
  useDeleteDealDocumentMutation,
  useGetFactorRequestsStatusesQuery,
  useGetFactorRequestsQuery,
  useGetCommissionCalculationTypesQuery,
  useLazyGetCommissionCalculationTypesQuery,
  useGetBuyerDealNumberQuery,
  useCreateBuyerDealMutation,
  useEditBuyerRequestMutation,
  useSendBuyerRequestMutation,
  useGetIncomingDealsQuery,
  useGetOutgoingDealsQuery,
  useGetFactorsBranchesQuery,
  useGetFactorsBranchInfoQuery,
  useCreateFactorsBranchMutation,
  useUpdateFactorsBranchMutation,
  useDeleteFactorsBranchMutation,
  useGetAttachedDealDocumentsQuery,
  useGetContractPartiesCompositionsQuery,
  useGetFactorsSettingsQuery,
  useUpdateFactorsSettingsMutation,
  useCreateFactorsConditionMutation,
  useGetFactorsConditionsQuery,
  useDeleteFactorsConditionMutation,
  useGetFactorsConditionInfoQuery,
  useUpdateFactorsConditionMutation,
  useLazyGetFactoringContractTemplateQuery,
  useLazyGetRegisterOfDebtsTemplateQuery,
  useLazyGetGeneralAgreementQuery,
  useLazyGetGeneralAgreementV2Query,
  useDeleteGeneralAgreementMutation,
  useGetGeneralAgreementV2Query,
  useAssignFactorDealBranchMutation,
  useGetNotificationsQuery,
  useViewNotificationMutation,
  useViewAllNotificationsMutation,
  useGetForeignContractorsQuery,
  useCreateForeignContractorMutation,
} = api;
